import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Headphones, Globe, Users, Code, PenTool, Award, CheckCircle } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export default function Services() {
  const { t } = useLanguage();

  const services = [
    {
      icon: Headphones,
      title: t('services.booking.title'),
      desc: t('services.booking.desc'),
      features: ['Reservas rápidas', 'Atendimento personalizado', 'Suporte dedicado']
    },
    {
      icon: Globe,
      title: t('services.support.title'),
      desc: t('services.support.desc'),
      features: ['Disponível 24/7', 'Emergência 365 dias', 'Web ativa 24h']
    },
    {
      icon: Users,
      title: t('services.commercial.title'),
      desc: t('services.commercial.desc'),
      features: ['Gestores regionais', 'Visitas presenciais', 'Contacto direto']
    },
    {
      icon: Code,
      title: t('services.technical.title'),
      desc: t('services.technical.desc'),
      features: ['Equipa IT própria', 'Integração XML', 'Suporte técnico']
    },
    {
      icon: PenTool,
      title: t('services.marketing.title'),
      desc: t('services.marketing.desc'),
      features: ['Catálogos personalizados', 'Ofertas exclusivas', 'Material promocional']
    },
    {
      icon: Award,
      title: t('services.quality.title'),
      desc: t('services.quality.desc'),
      features: ['Testes de qualidade', 'Seguimento exaustivo', 'Certificação Q']
    },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#1E3A8A] to-[#0F172A]" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 bg-[#00D4FF]/20 text-[#00D4FF] rounded-full text-sm font-medium mb-6">
              7x7 Travel
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {t('services.title')}
            </h1>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto">
              {t('services.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-[#F8FAFC]" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="group p-8 bg-white rounded-3xl border border-slate-100 hover:border-[#00D4FF]/30 hover:shadow-2xl transition-all duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                data-testid={`service-card-${index}`}
              >
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#1E3A8A] to-[#00D4FF] flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <service.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#0F172A] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {service.title}
                    </h3>
                    <p className="text-slate-600 leading-relaxed mb-6">
                      {service.desc}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-slate-700">
                          <CheckCircle className="w-5 h-5 text-[#00D4FF]" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-white" data-testid="why-choose">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Porquê a 7x7 Travel?
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { number: '01', title: 'Experiência', desc: 'Profissionais com mais de 20 anos de experiência no mercado turístico ibérico.' },
              { number: '02', title: 'Tecnologia', desc: 'Plataformas modernas e integração com os principais sistemas.' },
              { number: '03', title: 'Suporte', desc: 'Equipa dedicada disponível 24/7 para apoiar a sua agência.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="relative p-8 rounded-3xl bg-gradient-to-br from-slate-50 to-slate-100"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <span className="text-7xl font-bold text-[#1E3A8A]/10 absolute top-4 right-6" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {item.number}
                </span>
                <h3 className="text-xl font-bold text-[#0F172A] mb-3 relative z-10" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {item.title}
                </h3>
                <p className="text-slate-600 relative z-10">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
